import './modal-nominate-on-behalf.scss';
import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid2';
import apiClient from '../../../../utils/apiClient';
import { useNavigate } from 'react-router-dom';
import decodeToken from "../../../../utils/decode-token";

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 518,
  textAlign: 'center',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

function ModalNominateOnBehalf({ open, onClose }) {
  const { t } = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const userRole = decodeToken(token).role;
  const navigate = useNavigate();

  const [retailerAreaCode, setRetailerAreaCode] = useState('');
  const [retailerCity, setRetailerCity] = useState('');
  const [retailerId, setRetailerId] = useState('');
  const [selectedRetailerId, setSelectedRetailerId] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [retailers, setRetailers] = useState([]);
  const [filteredRetailers, setFilteredRetailers] = useState([]);
  const [uniqueCountries, setUniqueCountries] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [salespersonId, setSalespersonId] = useState('');
  const [errorMessage, setErrorMessage] = useState(false);
  const [formError, setFormError] = useState(false); // Nuevo estado para manejar errores en el formulario

  useEffect(() => {
    const fetchRetailers = async () => {

      let url = '';
      if (userRole === 'pma' || userRole === 'pmaGM') {
        url = `${process.env.REACT_APP_API_URL}/pma/retailers`;
      }
      if (
        userRole === 'admin' ||
        userRole === 'marketAdmin' ||
        userRole === 'countryAdmin' ||
        userRole === 'shippingAdmin' ||
        userRole === 'superAdmin'
      ) {
        url = `${process.env.REACT_APP_API_URL}/admin/retailers`;
      }
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
        const response = await apiClient.get(
          url,
          config
        );

        // Ordenar los retailers por el campo 'name' alfabéticamente
        const sortedRetailers = response.data.sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        setRetailers(sortedRetailers);

        const uniqueCountries = Array.from(
          new Set(response.data.map((retailer) => retailer.area_code))
        );
        setUniqueCountries(uniqueCountries);
      } catch (error) {
        console.error('Error fetching retailers:', error);
      }
    };

    fetchRetailers();
  }, [token]);

  useEffect(() => {
    let filtered = retailers;
    if (retailerAreaCode) {
      filtered = filtered.filter(
        (retailer) => retailer.area_code === retailerAreaCode
      );
    }
    if (citySearch) {
      filtered = filtered.filter((retailer) =>
        retailer.city.toLowerCase().includes(citySearch.toLowerCase())
      );
    }
    setFilteredRetailers(filtered);
  }, [retailerAreaCode, citySearch, retailers]);

  const handleRetailerChange = async (e) => {
    setSelectedRetailerId(e.target.value);
    setRetailerId(e.target.value);
    setSalespersons([]);
    setErrorMessage(false);
    setFormError(false); // Eliminar el error del formulario al seleccionar un retailer

    const selectedRetailer = retailers.find(
      (retailer) => retailer.id === parseInt(e.target.value)
    );

    if (selectedRetailer) {
      setRetailerAreaCode(selectedRetailer.area_code);
      setRetailerCity(selectedRetailer.city);

      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
        const response = await apiClient.get(
          `${process.env.REACT_APP_API_URL}/retailer/staff?retailerId=${e.target.value}`,
          config
        );
        if (response.data.length === 0) {
          setErrorMessage(true);
        } else {
          setSalespersons(response.data);
          setErrorMessage(false);
        }
      } catch (error) {
        console.error('Error fetching salespersons:', error);
        setErrorMessage(true);
      }
    }
  };

  const handleCitySearchChange = (e) => {
    setCitySearch(e.target.value);
    setRetailerCity(e.target.value);
  };

  const handleSalespersonChange = (e) => {
    setSalespersonId(e.target.value);
    setFormError(false); // Eliminar error si seleccionan un salesperson
  };

  const handleContinue = () => {
    if (!salespersonId) {
      // Si no hay un salesperson seleccionado, mostramos el error
      setFormError(true);
      return;
    }

    // Navegamos si no hay error
    navigate(`/admin/nominate?salesperson_id=${salespersonId}&retailer_id=${selectedRetailerId}`
      , {
        state: {
          areaCode: retailerAreaCode
        }
    });
  };

  return (
    <Modal open={open} onClose={onClose} className="on-behalf">
      <Box sx={style}>
        <button
          className="close"
          onClick={() => {
            onClose();
          }}
        >
          <span className="icon-close"></span>
        </button>
        <h2>{t('admin-nomination-title')}</h2>
        <p>{t('admin-nomination-text')}</p>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 12, md: 6 }}>
            <div className="form-control">
              <label htmlFor="retailer_area_code">{t('common-country')}</label>
              <select
                name="retailer_area_code"
                id="retailer_area_code"
                value={retailerAreaCode}
                onChange={(e) => setRetailerAreaCode(e.target.value)}
                className="normal"
              >
                <option value="">{t('common-select-country')}</option>
                {uniqueCountries.map((countryCode) => (
                  <option key={countryCode} value={countryCode}>
                    {t(`countries-${countryCode}`)}
                  </option>
                ))}
              </select>
            </div>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 6 }}>
            <div className="form-control">
              <label htmlFor="retailer_city">{t('common-city')}</label>
              <input
                type="text"
                id="retailer_city"
                name="retailer_city"
                value={retailerCity}
                onChange={handleCitySearchChange}
              />
            </div>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12 }}>
            <div className="form-control">
              <label htmlFor="retailer_name">{t('common-retailer-name')}</label>
              <select
                name="retailer_name"
                id="retailer_name"
                value={retailerId}
                onChange={handleRetailerChange}
                className="normal"
              >
                <option value="">{t('common-select-retailer')}</option>
                {filteredRetailers.map((retailer) => (
                  <option key={retailer.id} value={retailer.id}>
                    {retailer.name} ({retailer.city})
                  </option>
                ))}
              </select>
            </div>
          </Grid>
          {retailerId && (
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
              <div className={`form-control ${formError ? 'error' : ''}`}>
                <label htmlFor="salesperson_name">
                  {t('common-role-saleperson')}
                </label>
                {salespersons.length > 0 ? (
                  <select
                    name="salesperson_name"
                    id="salesperson_name"
                    className="normal"
                    value={salespersonId}
                    onChange={handleSalespersonChange}
                  >
                    <option value="">{t('common-select-saleperson')}</option>
                    {salespersons.map((salesperson) => (
                      <option key={salesperson.id} value={salesperson.id}>
                        {`${salesperson.name} ${salesperson.last_name}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className={errorMessage ? 'error' : ''}>
                    {t('common-no-salesperson')}
                  </p>
                )}
              </div>
            </Grid>
          )}
        </Grid>
        <div className="actions">
          <button className="btn secondary" onClick={onClose}>
            {t('common-cancel')}
          </button>
          <button className="btn" onClick={handleContinue}>
            {t('common-continue')}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

export default ModalNominateOnBehalf;
